import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  type StorageConfigInput,
  StorageConfigInputSchema,
  type StorageType,
} from '@innermate/shared';
import { type FormEvent, type ReactNode, useState } from 'react';

const S3_DEFAULTS = {
  type: 's3' as const,
  endpoint: 'http://localhost:9234',
  accessKey: 'innermate',
  secretKey: '',
  bucket: 'innermate',
  region: 'us-east-1',
  forcePathStyle: true,
  /** DuckDB 같이 로컬 파일 접근이 필요한 사본의 캐시 디렉터리. 빈 문자열이면 OS tmpdir. */
  tempCachePath: '',
};
const FS_DEFAULTS = { type: 'fs' as const, rootPath: '/var/innermate/uploads' };
const NFS_DEFAULTS = { type: 'nfs' as const, rootPath: '/mnt/innermate-nfs' };

export interface StorageBackendFormProps {
  /** 초기 type — 현재 설정의 type 또는 기본값. */
  initialType: StorageType;
  /**
   * S3 폼의 accessKey 마스킹 힌트 (예: "minio_admi…"). 편집 모드에선 사용자가 새 값을
   * 입력할 때까지 빈칸으로 두고, 비교용으로만 표시.
   */
  accessKeyMaskedHint?: string | null;
  /**
   * S3 백엔드의 임시 캐시 디렉터리 prefill. 편집 모드에서 기존 설정 보존 용도.
   * null/undefined 면 빈 문자열로 시작.
   */
  initialTempCachePath?: string | null;
  /** 부모가 결과를 받는 콜백 — submit 시 검증된 union 객체. */
  onSubmit: (config: StorageConfigInput) => void;
  /** 연결 테스트 버튼 — 검증된 config 를 부모에게 넘김. */
  onTest?: (config: StorageConfigInput) => void;
  /** 저장 버튼 라벨 (기본: "저장"). */
  submitLabel?: string;
  /** 저장/테스트 진행 중 비활성화. */
  busy?: boolean;
  testing?: boolean;
  /** 부모에서 mutation 결과로 표시할 인라인 결과 (성공/실패 메시지). */
  testResult?: { ok: boolean; message: string } | null;
  /** 서버에서 받은 에러 메시지 (zod 외 일반 에러). */
  serverError?: string | null;
  /** 좌측에 추가 액션 — 온보딩의 '건너뛰기' 버튼 등. */
  leftAction?: ReactNode;
}

export function StorageBackendForm(props: StorageBackendFormProps) {
  const [type, setType] = useState<StorageType>(props.initialType);
  const [s3Form, setS3Form] = useState({
    ...S3_DEFAULTS,
    tempCachePath: props.initialTempCachePath ?? '',
  });
  const [fsForm, setFsForm] = useState({ ...FS_DEFAULTS });
  const [nfsForm, setNfsForm] = useState({ ...NFS_DEFAULTS });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buildPayload = (): StorageConfigInput | null => {
    if (type === 's3') return { ...s3Form, type: 's3' as const };
    if (type === 'fs') return { ...fsForm, type: 'fs' as const };
    return { ...nfsForm, type: 'nfs' as const };
  };

  const validate = (): StorageConfigInput | null => {
    setErrors({});
    const draft = buildPayload();
    const parsed = StorageConfigInputSchema.safeParse(draft);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        errs[String(issue.path[0])] = issue.message;
      }
      setErrors(errs);
      return null;
    }
    return parsed.data;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const data = validate();
    if (data) props.onSubmit(data);
  };

  const test = () => {
    const data = validate();
    if (data && props.onTest) props.onTest(data);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <BackendTabs value={type} onChange={setType} />

      {type === 's3' ? (
        <S3Fields
          form={s3Form}
          setForm={setS3Form}
          errors={errors}
          accessKeyMaskedHint={props.accessKeyMaskedHint}
        />
      ) : null}
      {type === 'fs' ? <FsFields form={fsForm} setForm={setFsForm} errors={errors} /> : null}
      {type === 'nfs' ? <NfsFields form={nfsForm} setForm={setNfsForm} errors={errors} /> : null}

      {props.testResult ? (
        <div
          role="alert"
          className={`rounded-md border px-3 py-2 text-sm ${
            props.testResult.ok
              ? 'border-[hsl(140_60%_45%)]/30 bg-[hsl(140_60%_45%)]/5 text-[hsl(140_60%_35%)]'
              : 'border-destructive/30 bg-destructive/5 text-destructive'
          }`}
        >
          {props.testResult.message}
        </div>
      ) : null}

      {props.serverError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {props.serverError}
        </div>
      ) : null}

      <div className="flex items-center justify-between pt-2">
        <div>{props.leftAction}</div>
        <div className="flex items-center gap-2">
          {props.onTest ? (
            <Button type="button" variant="secondary" onClick={test} disabled={props.busy}>
              {props.testing ? '테스트 중…' : '연결 테스트'}
            </Button>
          ) : null}
          <Button type="submit" disabled={props.busy}>
            {props.submitLabel ?? '저장'}
          </Button>
        </div>
      </div>
    </form>
  );
}

function BackendTabs({
  value,
  onChange,
}: { value: StorageType; onChange: (t: StorageType) => void }) {
  const tabs: { type: StorageType; label: string }[] = [
    { type: 's3', label: 'S3 호환 객체 스토리지' },
    { type: 'nfs', label: 'NFS' },
    { type: 'fs', label: 'Local 파일시스템' },
  ];
  return (
    <div className="flex gap-2 border-b border-border">
      {tabs.map((t) => {
        const active = value === t.type;
        return (
          <button
            key={t.type}
            type="button"
            onClick={() => onChange(t.type)}
            className={
              active
                ? 'px-4 py-2 text-sm font-medium border-b-2 border-accent text-foreground -mb-px'
                : 'px-4 py-2 text-sm text-foreground-muted hover:text-foreground'
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function S3Fields({
  form,
  setForm,
  errors,
  accessKeyMaskedHint,
}: {
  form: typeof S3_DEFAULTS;
  setForm: (next: typeof S3_DEFAULTS) => void;
  errors: Record<string, string>;
  accessKeyMaskedHint?: string | null;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-foreground-muted">
        MinIO / SeaweedFS / AWS S3 등 S3 호환 객체 스토리지에 연결합니다.
      </p>
      <Field
        label="엔드포인트"
        htmlFor="sto-endpoint"
        hint="S3 API 주소 (예: http://minio:9000)"
        error={errors.endpoint}
      >
        <Input
          id="sto-endpoint"
          value={form.endpoint}
          onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
          autoComplete="off"
        />
      </Field>
      <Field
        label="Access Key"
        htmlFor="sto-access"
        hint={accessKeyMaskedHint ? `현재: ${accessKeyMaskedHint}` : undefined}
        error={errors.accessKey}
      >
        <Input
          id="sto-access"
          value={form.accessKey}
          onChange={(e) => setForm({ ...form, accessKey: e.target.value })}
          autoComplete="off"
        />
      </Field>
      <Field
        label="Secret Key"
        htmlFor="sto-secret"
        hint="암호화되어 저장됩니다. 매 저장 시 다시 입력해야 합니다."
        error={errors.secretKey}
      >
        <Input
          id="sto-secret"
          type="password"
          value={form.secretKey}
          onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
          autoComplete="new-password"
        />
      </Field>
      <Field label="버킷" htmlFor="sto-bucket" hint="없으면 자동 생성" error={errors.bucket}>
        <Input
          id="sto-bucket"
          value={form.bucket}
          onChange={(e) => setForm({ ...form, bucket: e.target.value })}
          autoComplete="off"
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="리전" htmlFor="sto-region" error={errors.region}>
          <Input
            id="sto-region"
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            autoComplete="off"
          />
        </Field>
        <Field label="Path style" htmlFor="sto-path" hint="MinIO/SeaweedFS 는 켜두세요">
          <div className="flex items-center gap-2 h-9">
            <input
              id="sto-path"
              type="checkbox"
              checked={form.forcePathStyle}
              onChange={(e) => setForm({ ...form, forcePathStyle: e.target.checked })}
            />
            <label htmlFor="sto-path" className="text-sm">
              {form.forcePathStyle ? '사용' : '사용 안 함'}
            </label>
          </div>
        </Field>
      </div>
      <Field
        label="임시 캐시 디렉터리 (선택)"
        htmlFor="sto-temp-cache"
        hint="DuckDB 등 로컬 파일이 필요한 사본을 보관할 절대 경로. 비워두면 OS 기본 tmpdir."
        error={errors.tempCachePath}
      >
        <Input
          id="sto-temp-cache"
          value={form.tempCachePath ?? ''}
          onChange={(e) => setForm({ ...form, tempCachePath: e.target.value })}
          placeholder="예: /var/lib/innermate/duckdb-cache"
          autoComplete="off"
        />
      </Field>
    </div>
  );
}

function FsFields({
  form,
  setForm,
  errors,
}: {
  form: typeof FS_DEFAULTS;
  setForm: (next: typeof FS_DEFAULTS) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-2.5 text-sm text-foreground-muted">
        서버가 설치된 머신의 로컬 디스크 디렉터리입니다. 절대 경로로 입력해 주세요. 디렉터리가
        없으면 앱이 자동으로 생성합니다 (권한이 충분한 경우).
      </div>
      <Field
        label="저장 디렉터리 경로"
        htmlFor="sto-rootpath"
        hint="예: /var/innermate/uploads"
        error={errors.rootPath}
      >
        <Input
          id="sto-rootpath"
          value={form.rootPath}
          onChange={(e) => setForm({ ...form, rootPath: e.target.value })}
          autoComplete="off"
        />
      </Field>
    </div>
  );
}

function NfsFields({
  form,
  setForm,
  errors,
}: {
  form: typeof NFS_DEFAULTS;
  setForm: (next: typeof NFS_DEFAULTS) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-2.5 text-sm text-foreground-muted">
        OS 에서 NFS 를 미리 마운트한 마운트포인트를 입력해 주세요. 앱은 mount 명령을 실행하지
        않습니다. 예:{' '}
        <code className="font-mono">mount -t nfs server:/export/innermate /mnt/innermate-nfs</code>
      </div>
      <Field
        label="NFS 마운트 경로"
        htmlFor="sto-nfsroot"
        hint="예: /mnt/innermate-nfs"
        error={errors.rootPath}
      >
        <Input
          id="sto-nfsroot"
          value={form.rootPath}
          onChange={(e) => setForm({ ...form, rootPath: e.target.value })}
          autoComplete="off"
        />
      </Field>
    </div>
  );
}
